import React from 'react'
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from '@mui/material/styles'
import { labravDefaultThemeV7 } from '@labrav/react-components'
import { render, RenderOptions } from '@testing-library/react'
import type { Store } from 'redux'
import { Provider } from 'react-redux'
import { FlagsProvider } from '@labrav/flags'
import { MemoryRouter } from 'react-router-dom'

import bundledDefaultFlagsJson from '../../flags/dev/flags.json'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export type LabraWrappers = {
  flagsJson?: Record<string, unknown>
  store?: Store
  initialRoute?: string
}

export type CreateLabraRenderConfig = {
  getStore: () => Store
  defaultFlagsJson?: Record<string, unknown>
}

export type LabraRenderOptions = RenderOptions & {
  labraWrappers?: LabraWrappers
}

export const createLabraRender = ({
  getStore,
  defaultFlagsJson = bundledDefaultFlagsJson as Record<string, unknown>,
}: CreateLabraRenderConfig) => {
  const AllTheProviders: (
    labraWrappers?: LabraWrappers
  ) => React.FC<{ children: React.ReactNode }> =
    options =>
    ({ children }) => {
      const store = options?.store || getStore()
      const flagsJson = options?.flagsJson || defaultFlagsJson
      const initialRoutes = options?.initialRoute
        ? [options.initialRoute]
        : undefined

      return (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={labravDefaultThemeV7}>
            <Provider store={store}>
              <MemoryRouter initialEntries={initialRoutes}>
                <FlagsProvider
                  flagsUrl=""
                  flagsJson={flagsJson}
                  service="webapp"
                  env="test"
                >
                  {children}
                </FlagsProvider>
              </MemoryRouter>
            </Provider>
          </ThemeProvider>
        </StyledEngineProvider>
      )
    }

  const customRender = (ui: React.ReactElement, options?: LabraRenderOptions) =>
    render(ui, {
      wrapper: AllTheProviders(options?.labraWrappers) as React.ComponentType,
      ...options,
    })

  return customRender
}

/** @deprecated Use `createLabraRender` and export `render` from your app test setup. */
export const createLabraRenderV4 = createLabraRender
